import { createFeatureSelector, createSelector } from '@ngrx/store';

import * as GoalFeatureState from './goal.state';

export const selectGoalState =
  createFeatureSelector<GoalFeatureState.GoalState>(
    GoalFeatureState.goalFeatureKey
  );
export const selectGoals = createSelector(
  selectGoalState,
  (state) => state.userGoals
);

/**
 * GR.6. Whether the goals have been read from the server at all. Before that,
 * "no career goal" is not known, only not loaded, so nothing may write one.
 */
export const selectGoalsHaveLoaded = createSelector(
  selectGoalState,
  (state) => !!state?.hasLoaded
);

export const selectAiDeepInsights = createSelector(
  selectGoalState,
  (state) => state?.aiDeepInsights
);

export const selectCertificates = createSelector(
  selectAiDeepInsights,
  (aiDeepInsights) => aiDeepInsights?.learningSuggestions?.certificates
);

export const selectCourses = createSelector(
  selectAiDeepInsights,
  (aiDeepInsights) => aiDeepInsights?.learningSuggestions?.courses
);

/*
 * A GOAL IS SET WHEN ITS ID IS SET. THE TITLE IS A LABEL.
 *
 * All three of these required a title as well as an id, and the title is not
 * part of the goal's identity: it is what a screen shows once the goal is
 * loaded. `setCredentialGoal` saves a program id and an education level and
 * no title at all, because a row from `programsByOccupation` is a credential
 * rather than a named school's copy of one.
 *
 * So the member pressed Set goal, the API accepted it, the dialog said
 * Congratulations, and `isAcademicInstitutionProgramGoalSet` stayed false
 * because no title had come back. The school step is rendered behind that
 * flag, so it never unlocked: the reported "Congratulations confirmation but
 * behaves oddly", and the goal-setting that was "blocking users from reaching
 * institution selection".
 *
 * The career goal had the same shape and the same consequence on the
 * occupation page, where a goal whose stored title was worded differently
 * read as not set at all.
 */
export const selectIsCareerGoalSet = createSelector(
  selectGoals,
  (userGoals) => !!userGoals?.onetCode
);

export const selectIsStatusConnected = createSelector(
  selectGoalState,
  (goals) => goals.statusConnect
);

export const selectInstitutionProgramGoal = createSelector(
  selectGoals,
  (userGoals) => ({
    schoolProgramId: userGoals?.schoolProgramId,
    schoolProgramEdLevelId: userGoals?.schoolProgramEdLevelId
  })
);

export const selectCertificationGoal = createSelector(
  selectGoals,
  (userGoals) => ({
    certificationId: userGoals?.certificationId,
    certificationTitle: userGoals?.certificationTitle
  })
);

export const selectLicenseGoal = createSelector(selectGoals, (userGoals) => ({
  licenseId: userGoals?.licenseId,
  licenseTitle: userGoals?.licenseTitle
}));

export const selectIsAcademicInstitutionGoalSet = createSelector(
  selectGoals,
  (userGoals) => !!userGoals?.schoolId
);

export const selectIsAcademicInstitutionProgramGoalSet = createSelector(
  selectGoals,
  (userGoals) =>
    !!userGoals?.schoolProgramId && !!userGoals?.schoolProgramEdLevelId
);

export const selectIsCertificationGoalSet = createSelector(
  selectGoals,
  (userGoals) => !!userGoals?.certificationId && !!userGoals?.certificationTitle
);

export const selectIsLicenseGoalSet = createSelector(
  selectGoals,
  (userGoals) => !!userGoals?.licenseId && !!userGoals?.licenseTitle
);

/**
 * CRED.1. A Credential Registry credential is set.
 *
 * ID ONLY, deliberately, unlike the certification and licence selectors above.
 * Requiring a title as well is the bug this file already carries a note about:
 * a member who had set a goal saw it read as unset whenever the title had not
 * arrived yet. Career and program were corrected to id-only; cert and licence
 * were not, and this one is written the corrected way rather than copying the
 * two that still have it.
 */
export const selectIsCredentialGoalSet = createSelector(
  selectGoals,
  (userGoals) => !!userGoals?.credentialCtid
);

/**
 * CPD-665. THE CREDENTIAL ROW, WHICHEVER COLUMN IS BEHIND IT.
 *
 * `goalRows` lists FIVE rows -- career, credential, program, school,
 * licensure -- and its credential row prints `credentialTitle ||
 * certificationTitle`. The counts below took the certification and the
 * Registry credential as two separate goals, so a member holding both read
 * "6/6" over a five-row checklist. Phong reached exactly that by setting one
 * of each.
 *
 * `credential-row.ts` now stops the two ever being held together. This
 * selector is what makes the header right for the rows ALREADY written that
 * way: they self-repair on the next goal write, and until then the count is
 * of rows on the checklist rather than of columns in the table.
 */
export const selectIsCredentialRowSet = createSelector(
  selectIsCredentialGoalSet,
  selectIsCertificationGoalSet,
  (isCredentialGoalSet, isCertificationGoalSet) =>
    isCredentialGoalSet || isCertificationGoalSet
);

export const selectIsAnyGoalSet = createSelector(
  selectIsCareerGoalSet,
  selectIsAcademicInstitutionGoalSet,
  selectIsAcademicInstitutionProgramGoalSet,
  selectIsCertificationGoalSet,
  selectIsLicenseGoalSet,
  selectIsCredentialGoalSet,
  (
    isCareerGoalSet,
    isAcademicInstitutionGoalSet,
    isAcademicInstitutionProgramGoalSet,
    isCertificationGoalSet,
    isLicenseGoalSet,
    isCredentialGoalSet
  ) =>
    isCareerGoalSet ||
    isAcademicInstitutionGoalSet ||
    isAcademicInstitutionProgramGoalSet ||
    isCertificationGoalSet ||
    isLicenseGoalSet ||
    isCredentialGoalSet
);

export const selectIsAcademicGoalSet = createSelector(
  selectIsCareerGoalSet,
  selectIsAcademicInstitutionGoalSet,
  selectIsAcademicInstitutionProgramGoalSet,
  (
    isCareerGoalSet,
    isAcademicInstitutionGoalSet,
    isAcademicInstitutionProgramGoalSet
  ) =>
    isCareerGoalSet &&
    isAcademicInstitutionGoalSet &&
    isAcademicInstitutionProgramGoalSet
);

/*
 * CPD-665: the credential row counts once, not once per column. Requiring both
 * columns here made this unreachable by construction the moment setting either
 * one cleared the other.
 */
export const selectAreAllGoalsSet = createSelector(
  selectIsCareerGoalSet,
  selectIsAcademicInstitutionGoalSet,
  selectIsAcademicInstitutionProgramGoalSet,
  selectIsCredentialRowSet,
  selectIsLicenseGoalSet,
  (
    isCareerGoalSet,
    isAcademicInstitutionGoalSet,
    isAcademicInstitutionProgramGoalSet,
    isCredentialRowSet,
    isLicenseGoalSet
  ) =>
    isCareerGoalSet &&
    isAcademicInstitutionGoalSet &&
    isAcademicInstitutionProgramGoalSet &&
    isCredentialRowSet &&
    isLicenseGoalSet
);

export const selectGoalCount = createSelector(
  selectIsCareerGoalSet,
  selectIsAcademicInstitutionGoalSet,
  selectIsAcademicInstitutionProgramGoalSet,
  selectIsCredentialRowSet,
  selectIsLicenseGoalSet,
  (
    isCareerGoalSet,
    isAcademicInstitutionGoalSet,
    isAcademicInstitutionProgramGoalSet,
    isCredentialRowSet,
    isLicenseGoalSet
  ) =>
    (isCareerGoalSet ? 1 : 0) +
    (isAcademicInstitutionGoalSet ? 1 : 0) +
    (isAcademicInstitutionProgramGoalSet ? 1 : 0) +
    (isCredentialRowSet ? 1 : 0) +
    (isLicenseGoalSet ? 1 : 0)
);

/*
 * The denominator behind "N of M goals" in the header.
 *
 * 3 is career, program and school: the goals the Goals Report is built from and
 * which every member is expected to set. The optional ones only join the total
 * once they are actually set, so the header never tells a member they are 3 of
 * 6 for things nobody asked them to do. CRED.1 adds the credential on the same
 * terms.
 *
 * CPD-665: which caps this at 5, the number of rows the checklist has. It read
 * 6 whenever a member held a certification and a Registry credential at once,
 * counting one row twice.
 */
export const selectTotalGoalCount = createSelector(
  selectIsCredentialRowSet,
  selectIsLicenseGoalSet,
  (isCredentialRowSet, isLicenseGoalSet) =>
    3 + (isCredentialRowSet ? 1 : 0) + (isLicenseGoalSet ? 1 : 0)
);

export const selectReportDownloaded = createSelector(
  selectGoalState,
  (state) => state.reportDownloaded
);

export const selectReportShared = createSelector(
  selectGoalState,
  (state) => state.reportShared
);

export const selectReportFile = createSelector(
  selectGoalState,
  (state) => state.reportFile
);

export const selectReturnLink = createSelector(
  selectGoalState,
  (state) => state.returnLink
);

export const selectDateGenerated = createSelector(
  selectGoalState,
  (state) => state.dateGenerated
);

export const selectLoadingGoalsReport = createSelector(
  selectGoalState,
  (state) => state.loadingGoalsReport
);

export const selectAiGoalsReport = createSelector(
  selectGoalState,
  (state) => state.aiGoalsReport
);

export const selectAiGoalsReportError = createSelector(
  selectGoalState,
  (state) => state.aiGoalsReportError ?? null
);

export const selectIsRefreshingAiGoalsReport = createSelector(
  selectGoalState,
  (state) => !!state.refreshingAiGoalsReport
);

export const selectLoadingAiDeepInsight = createSelector(
  selectGoalState,
  (state) => state.loadingAiDeepInsight
);
